import React from "react";
import { Card, Text } from "react-native-paper";

import TableContent from "../../../components/TableContent";
import TimelineContent from "../../../components/TimelineContent";
import { BulletsContent } from "../../../components/BulletsContent";

import ScenarioContent from "../components/ScenarioContent";
import StakeholderImpact from "../components/StakeholderImpact";

import { CONTENT_TYPES } from "../../../shared/constants/content-types";

import {
  IFullArticleDetails,
  IArticleDetail,
  IScenario,
  IStakeholderImpact,
  ITableContent,
  ITimelineItem,
  IConceptDecodedItem,
} from "../types";

import { NewsDetailsCard } from "../../immersive-news-article/components/NewsDetailsCard";

/* -------------------------------------------------------------------------- */
/* Types */
/* -------------------------------------------------------------------------- */

export interface CardData {
  key: string;
  title: string;
  content: React.ReactNode;
}

/* -------------------------------------------------------------------------- */
/* Shared Styles (avoid recreating objects repeatedly) */
/* -------------------------------------------------------------------------- */

const CARD_MARGIN_STYLE = {
  marginHorizontal: 20,
  marginVertical: 16,
};

const CARD_PADDING_STYLE = {
  padding: 16,
};

/* -------------------------------------------------------------------------- */
/* Render Content Helper */
/* -------------------------------------------------------------------------- */

export const renderContent = (
  type: string,
  content:
    | string[]
    | string
    | ITimelineItem[]
    | ITableContent[]
    | IStakeholderImpact[]
    | IScenario[]
    | IConceptDecodedItem[],
  theme: any,
  styles: object = {},
  showOnCard: boolean = false
) => {
  switch (type) {
    case CONTENT_TYPES.BULLETS:
      return showOnCard ? (
        <Card
          mode="elevated"
          style={{ ...styles, backgroundColor: theme?.colors?.surfaceBg }}
        >
          {(content as any[]).map((item, index) => (
            <BulletsContent
              key={`item-${index}`}
              heading={item.title}
              content={item.content as string[]}
            />
          ))}
        </Card>
      ) : (
        (content as any[]).map((item, index) => (
          <BulletsContent
            key={`item-${index}`}
            heading={item.title}
            content={item.content as string[]}
            style={styles}
          />
        ))
      );

    case CONTENT_TYPES.TIMELINE:
      return <TimelineContent timelineData={content as ITimelineItem[]} style={styles} />;

    case CONTENT_TYPES.TABLE:
      return <TableContent content={content as ITableContent[]} style={styles} />;

    case CONTENT_TYPES.STAKEHOLDER_IMPACT:
      return (
        <StakeholderImpact
          content={content as IStakeholderImpact[]}
          style={styles}
        />
      );

    case CONTENT_TYPES.SCENARIO:
      return <ScenarioContent content={content as IScenario[]} style={styles} />;

    case CONTENT_TYPES.CONCEPTS_DECODED: {
      const mappedContent = (content as IConceptDecodedItem[]).map((item) => ({
        title: item.title,
        description: item.content,
        // impact: "neutral",
        // sentiment: "neutral",
      })) as IStakeholderImpact[];

      return <StakeholderImpact content={mappedContent} style={styles} />;
    }

    default:
      return showOnCard ? (
        <Card
          mode="elevated"
          style={{
            ...CARD_PADDING_STYLE,
            ...styles,
            backgroundColor: theme?.colors?.surfaceBg,
          }}
        >
          <Text
            variant="bodyMedium"
            style={{ color: theme?.colors?.bodyText }}
          >
            {content?.toString()}
          </Text>
        </Card>
      ) : (
        <Text
          variant="bodyMedium"
          style={{ color: theme?.colors?.bodyText }}
        >
          {content?.toString()}
        </Text>
      );
  }
};

/* -------------------------------------------------------------------------- */
/* Create Immersive Article Cards */
/* -------------------------------------------------------------------------- */

export const createImmersiveArticleCards = (
  fullArticleDetails: IFullArticleDetails,
  theme: any,
  t: any,
  onShare?: () => void
): CardData[] => {
  const articlePages: CardData[] = [];

  /* ---------------------------- Story So Far ---------------------------- */

  const storySoFar = [...fullArticleDetails.storySoFar].sort(
    (a: IArticleDetail, b: IArticleDetail) => b.sequence - a.sequence
  );

  storySoFar.forEach((story) => {
    const backgroundColor =
      story.type !== CONTENT_TYPES.TIMELINE &&
      story.type !== CONTENT_TYPES.CONCEPTS_DECODED
        ? theme?.colors?.surfaceBg
        : "transparent";

    articlePages.push({
      key: `STORY_SO_FAR-${story.sequence}`,
      title:
        story.type === CONTENT_TYPES.TIMELINE
          ? t("screens.article.story_so_far")
          : t("screens.article.concepts_decoded"),
      content: renderContent(
        story.type,
        story.content,
        theme,
        { ...CARD_MARGIN_STYLE, backgroundColor },
        true
      ),
    });
  });

  /* ---------------------------- GIST Card ---------------------------- */

  articlePages.push({
    key: "GIST",
    title: t("screens.article.gist"),
    content: (
      <NewsDetailsCard
        id={fullArticleDetails.id}
        description={fullArticleDetails.description}
        impactTag={fullArticleDetails.impactTags?.[0]}
        contentType={fullArticleDetails.contentType}
        content={fullArticleDetails.gistContent}
        sourceCount={fullArticleDetails.sourceCount}
        favicons={fullArticleDetails.favicons}
        date={fullArticleDetails.date}
        image={fullArticleDetails.image}
        imageSourceUrl={fullArticleDetails.imageSourceUrl}
        title={fullArticleDetails.title}
        onShare={onShare}
      />
    ),
  });

  /* ---------------------------- What's Next ---------------------------- */

  const whatsNext = [...fullArticleDetails.whatsNext].sort(
    (a: IArticleDetail, b: IArticleDetail) => a.sequence - b.sequence
  );

  whatsNext.forEach((story) => {
    const isRipple =
      story.type === CONTENT_TYPES.STAKEHOLDER_IMPACT;

    articlePages.push({
      key: `${isRipple ? "RIPPLE_EFFECT" : "NEXT_DROP"}-${story.sequence}`,
      title: isRipple
        ? t("screens.article.ripple_effect")
        : t("screens.article.next_drop"),
      content: renderContent(
        story.type,
        story.content,
        theme,
        CARD_MARGIN_STYLE,
        true
      ),
    });
  });

  return articlePages;
};